import { CURRENT_PERMITS_PER_10K, type CoverageStats } from '@mollusk/shared';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { approxShare, millionsFloor, millionsRounded } from './format';

export const STAT_DEFINITIONS = {
  permits:
    'Building permits we hold from the jurisdictions we cover, cleaned, normalized and tagged.',
  last12:
    "Permits dated in the 12 months up to each jurisdiction's newest permit, across every jurisdiction we cover.",
  people:
    'People living in a jurisdiction whose permits we hold with recent, plausible volume (Census 2023 estimates), and their share of the US population.',
  cities: `Current permit data means the city is covered, its feed is current, and it has ${CURRENT_PERMITS_PER_10K}+ permits per 10,000 residents in the last 12 months.`,
};

const CARDS = [
  { to: '/coverage', title: 'Coverage', text: 'Which jurisdictions we cover, and how deep.' },
  { to: '/explore', title: 'Explore', text: 'Permits by year and top tags for any place.' },
  { to: '/tags', title: 'Tags', text: 'What each tag means and where it fires.' },
  { to: '/sample-data', title: 'Sample data', text: 'Download evaluation files.' },
  { to: '/data-dictionary', title: 'Data dictionary', text: 'Every field and tag, defined.' },
];

function Stat({ value, label, tip }: { value: string; label: string; tip: string }) {
  return (
    <Tooltip title={tip} arrow>
      <Paper variant="outlined" sx={{ p: 2, flex: '1 1 200px', cursor: 'help' }}>
        <Typography variant="h5" component="p" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Paper>
    </Tooltip>
  );
}

/** The prospect landing page (#171). `search` is the search box element. */
export function HomeView({
  stats,
  search,
  onNavigate,
}: {
  stats: CoverageStats | undefined;
  search: ReactNode;
  onNavigate: (to: string) => void;
}) {
  const s = stats;
  const top50 = s?.large_cities.top50;
  return (
    <Box>
      <Typography
        variant="h3"
        component="h1"
        sx={{ fontWeight: 600, fontSize: { xs: 30, sm: 44 } }}
      >
        Building permits, cleaned and tagged.
      </Typography>
      <Typography
        variant="h6"
        component="p"
        color="text.secondary"
        sx={{ mt: 1, mb: 3, fontWeight: 400 }}
      >
        {s
          ? `${millionsFloor(s.permits_on_record)} permits from jurisdictions covering ${millionsRounded(s.people_covered)} people, ${approxShare(s.share_covered)} of the US, normalized and tagged by scope, trade, equipment and property type.`
          : 'Permits normalized and tagged by scope, trade, equipment and property type.'}
      </Typography>
      <Box sx={{ mb: 4 }}>{search}</Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Stat
          value={s ? s.permits_on_record.toLocaleString() : '—'}
          label="permits on record"
          tip={STAT_DEFINITIONS.permits}
        />
        <Stat
          value={s ? s.permits_last_12mo.toLocaleString() : '—'}
          label="permits in the last 12 months"
          tip={STAT_DEFINITIONS.last12}
        />
        <Stat
          value={
            s
              ? `${Math.round(s.people_covered / 1_000_000)}M (${Math.round(s.share_covered * 100)}%)`
              : '—'
          }
          label="people covered (share of the US)"
          tip={STAT_DEFINITIONS.people}
        />
        <Stat
          value={top50 ? `${top50.ok} of ${top50.total}` : '—'}
          label="largest cities with current permit data"
          tip={STAT_DEFINITIONS.cities}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Data as of {s?.as_of ?? '—'}
      </Typography>
      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
        }}
      >
        {CARDS.map((c) => (
          <Paper key={c.to} variant="outlined">
            <ButtonBase
              onClick={() => onNavigate(c.to)}
              sx={{
                p: 2,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                textAlign: 'left',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>{c.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {c.text}
              </Typography>
            </ButtonBase>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
